import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, DialogActions, DialogContent,
  Grid, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RateGrid from '../../../components/shared/pricing/RateGrid';
import { fmtDollar, fmtPricingPct } from '../../../components/shared/pricing/pricingUtils';
import loansApi from '../../../services/loansApi';
import { getAxiosErrorMessage } from '../../../utils/getAxiosErrorMessage';

const ADVISOR_DISCLAIMER =
  'Illustrative pricing recommendation — not an underwriting approval or AUS finding.';

const fmtDateTime = (v) => (v ? new Date(v).toLocaleString() : null);

const parseTargetPrice = (value) => {
  if (value === '' || value == null) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const buildAdvisorOptions = ({
  lockDuration,
  compensationType,
  selectedFamilies,
  selectedTerms,
  confirmedFico,
  targetPrice,
}) => ({
  lockTerm: lockDuration,
  compensationType,
  productFamilies: selectedFamilies,
  terms: selectedTerms,
  confirmedFico: confirmedFico ? Number(confirmedFico) : undefined,
  targetPrice: parseTargetPrice(targetPrice),
});

const pickClosestPriceRow = (product, lockTerm, targetPrice) => {
  const rows = (product?.prices || []).filter((r) => !lockTerm || r.lockTerm === lockTerm);
  if (!rows.length) return null;
  if (targetPrice == null || !Number.isFinite(targetPrice)) return rows[0];
  return [...rows].sort(
    (a, b) => Math.abs((a.adjustedPrice ?? 0) - targetPrice) - Math.abs((b.adjustedPrice ?? 0) - targetPrice),
  )[0];
};

const MismoAdvisorStep = ({
  selectedFile,
  lockDuration,
  compensationType,
  selectedFamilies,
  selectedTerms,
  bpcCalcMethod,
  bpcValue,
  onAgree,
  onDisagree,
  onImportAndLoadProducts,
  onBack,
  loading: parentLoading,
}) => {
  const [previewMismoAdvisor] = loansApi.endpoints.previewMismoAdvisor.useMutation();
  const [confirmedFico, setConfirmedFico] = useState('');
  const [targetPrice, setTargetPrice] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [disagreeMode, setDisagreeMode] = useState(false);
  const [overrideRate, setOverrideRate] = useState(null);
  const [showAllRates, setShowAllRates] = useState(false);

  // Manual bypass (when OB pre-import pricing fails)
  const [manualMode, setManualMode] = useState(false);
  const [manualProducts, setManualProducts] = useState([]);
  const [manualPricingRequest, setManualPricingRequest] = useState(null);
  const [importedLoanId, setImportedLoanId] = useState(null);
  const [importedLoanNumber, setImportedLoanNumber] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [manualRate, setManualRate] = useState('');
  const [manualPrice, setManualPrice] = useState('');
  const [manualLockTerm, setManualLockTerm] = useState(lockDuration || 30);
  const [selectedManualRate, setSelectedManualRate] = useState(null);
  const [showAllManualRates, setShowAllManualRates] = useState(false);

  const needsFico = preview?.missingFields?.includes('representativeFico');
  const recommendation = preview?.recommendation;
  const analysis = preview?.analysis;
  const hasRecommendation = recommendation?.hasRecommendation;
  const pricingQuotedAt = preview?.pricingResponse?.quotedAt;
  const pricingError = preview?.pricingError || error;

  const ficoValid = !needsFico || (
    confirmedFico
    && Number(confirmedFico) >= 300
    && Number(confirmedFico) <= 850
  );

  const advisorOptions = useMemo(
    () => buildAdvisorOptions({
      lockDuration,
      compensationType,
      selectedFamilies,
      selectedTerms,
      confirmedFico,
      targetPrice,
    }),
    [lockDuration, compensationType, selectedFamilies, selectedTerms, confirmedFico, targetPrice],
  );

  const runPreview = useCallback(async () => {
    if (!selectedFile) return;
    if (needsFico && !ficoValid) return;

    setLoading(true);
    setError(null);
    try {
      const result = await previewMismoAdvisor({
        file: selectedFile,
        lockTerm: lockDuration,
        compensationType,
        productFamilies: selectedFamilies,
        terms: selectedTerms,
        confirmedFico: confirmedFico ? Number(confirmedFico) : undefined,
        targetPrice: parseTargetPrice(targetPrice),
      }).unwrap();
      setPreview(result);
      setDisagreeMode(false);
      setOverrideRate(null);
      if (result.pricingError) {
        setError(result.pricingError);
        setManualMode(true);
      }
    } catch (err) {
      setError(getAxiosErrorMessage(err, 'Failed to analyze MISMO file.'));
      setManualMode(true);
    } finally {
      setLoading(false);
    }
  }, [
    selectedFile,
    lockDuration,
    compensationType,
    selectedFamilies,
    selectedTerms,
    confirmedFico,
    targetPrice,
    needsFico,
    ficoValid,
    previewMismoAdvisor,
  ]);

  useEffect(() => {
    if (!selectedFile || preview) return;
    runPreview();
  // Auto-preview once when a file is chosen; re-running on preview/runPreview churn would loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedFile is the intentional trigger
  }, [selectedFile]);

  useEffect(() => {
    setManualLockTerm(lockDuration || 30);
  }, [lockDuration]);

  const recommendedProduct = useMemo(() => {
    if (!hasRecommendation || !preview?.pricingResponse?.products) return null;
    return preview.pricingResponse.products.find(
      (p) => p.productId === recommendation.productId,
    );
  }, [preview, recommendation, hasRecommendation]);

  const selectedManualProduct = useMemo(
    () => manualProducts.find((p) => p.productId === selectedProductId) || null,
    [manualProducts, selectedProductId],
  );

  const applyProductDefaults = useCallback((product, lockTerm, target) => {
    if (!product) return;
    setSelectedProductId(product.productId);
    const row = pickClosestPriceRow(product, lockTerm, parseTargetPrice(target));
    if (row) {
      setSelectedManualRate({
        ...row,
        productId: product.productId,
        productShortName: product.productShortName,
        productName: product.productName,
      });
      setManualRate(String(row.rate ?? ''));
      setManualPrice(String(row.adjustedPrice ?? ''));
      if (row.lockTerm) setManualLockTerm(row.lockTerm);
    } else {
      setSelectedManualRate(null);
      setManualRate('');
      setManualPrice('');
    }
  }, []);

  const handleLoadProducts = async () => {
    if (!onImportAndLoadProducts) return;
    setError(null);
    try {
      const result = await onImportAndLoadProducts({
        advisorOptions,
        confirmedFico: confirmedFico ? Number(confirmedFico) : undefined,
      });
      setImportedLoanId(result.loanId);
      setImportedLoanNumber(result.loanNumber);
      setManualProducts(result.products || []);
      setManualPricingRequest(result.pricingRequest || null);
      if (result.analysis) {
        setPreview((prev) => ({
          ...(prev || {}),
          analysis: result.analysis,
          pricingRequest: result.pricingRequest,
          pricingResponse: result.pricingResponse,
          recommendation: result.recommendation,
          pricingError: null,
        }));
      }
      if (result.products?.length) {
        const preferred = result.recommendation?.productId
          ? result.products.find((p) => p.productId === result.recommendation.productId)
          : result.products.find((p) => p.isEligible) || result.products[0];
        applyProductDefaults(preferred, manualLockTerm, targetPrice);
      }
      if (result.pricingError) {
        setError(result.pricingError);
      }
      setManualMode(true);
    } catch (err) {
      setError(getAxiosErrorMessage(err, 'Failed to import loan and load products.'));
      setManualMode(true);
    }
  };

  const handleAgree = () => {
    const rate = disagreeMode && overrideRate
      ? {
        productId: overrideRate.productId || recommendation.productId,
        productShortName: overrideRate.productShortName || recommendation.productShortName,
        productName: overrideRate.productName || recommendation.productName,
        rate: overrideRate.rate,
        adjustedPrice: overrideRate.adjustedPrice,
        lockTerm: overrideRate.lockTerm || lockDuration,
        quoteId: overrideRate.quoteId,
      }
      : {
        productId: recommendation.productId,
        productShortName: recommendation.productShortName,
        productName: recommendation.productName,
        rate: recommendation.rate,
        adjustedPrice: recommendation.adjustedPrice,
        lockTerm: recommendation.lockTerm || lockDuration,
        quoteId: recommendation.quoteId,
      };
    onAgree({
      preview,
      advisorOptions,
      assignPayload: {
        pricingRequest: preview.pricingRequest,
        productId: rate.productId,
        rate: rate.rate,
        price: rate.adjustedPrice,
        lockTerm: rate.lockTerm || lockDuration,
        ...(rate.quoteId ? { quoteId: rate.quoteId } : {}),
        ...(compensationType === 'BPC' && bpcValue
          ? { bpcCalcMethod, bpcValue: parseFloat(bpcValue) }
          : {}),
      },
    });
  };

  const handleDisagree = () => {
    if (!disagreeMode) {
      setDisagreeMode(true);
      return;
    }
    if (!overrideRate) {
      setError('Select a rate from the grid below.');
      return;
    }
    onDisagree({
      preview,
      advisorOptions,
      preferredProductShortName: overrideRate.productShortName,
      preferredRate: overrideRate.rate,
      pricingFilters: {
        lockDuration,
        compensationType,
        selectedFamilies,
        selectedTerms,
        preferredProductShortName: overrideRate.productShortName,
        preferredRate: overrideRate.rate,
        ...(compensationType === 'BPC' ? { bpcCalcMethod, bpcValue } : {}),
      },
    });
  };

  const handleManualConfirm = () => {
    const rate = Number(manualRate);
    const price = Number(manualPrice);
    if (!selectedManualProduct) {
      setError('Select a product from the Optimal Blue results.');
      return;
    }
    if (!Number.isFinite(rate) || rate <= 0) {
      setError('Select a valid rate.');
      return;
    }
    if (!Number.isFinite(price) || price <= 0) {
      setError('Select a valid price.');
      return;
    }

    const quoteId = selectedManualRate?.quoteId;
    if (!importedLoanId || !quoteId || !manualPricingRequest || !onAgree) {
      setError('This quote has no Optimal Blue quote id. Re-run Import loan & load products, then select a rate row.');
      return;
    }

    onAgree({
      preview: {
        ...(preview || {}),
        pricingRequest: manualPricingRequest,
      },
      advisorOptions,
      alreadyImported: {
        loanId: importedLoanId,
        loanNumber: importedLoanNumber,
      },
      assignPayload: {
        pricingRequest: manualPricingRequest,
        productId: selectedManualProduct.productId,
        rate,
        price,
        lockTerm: manualLockTerm,
        quoteId,
        ...(compensationType === 'BPC' && bpcValue
          ? { bpcCalcMethod, bpcValue: parseFloat(bpcValue) }
          : {}),
      },
    });
  };

  const busy = loading || parentLoading;
  const showPricingInputs = !loading && (!!preview || !!error);
  const canUseRecommendation = hasRecommendation && !manualMode;

  return (
    <>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>{ADVISOR_DISCLAIMER}</Alert>
        {pricingError && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            onClose={() => setError(null)}
            action={!manualMode ? (
              <Button color="inherit" size="small" onClick={() => setManualMode(true)}>
                Choose manually
              </Button>
            ) : undefined}
          >
            {pricingError}
          </Alert>
        )}

        {showPricingInputs && !manualMode && (
          <Box sx={{ mb: 2 }}>
            {needsFico && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Credit score was not found in the MISMO file. Enter representative FICO to run pricing.
                </Typography>
                <TextField
                  label="Representative FICO"
                  type="number"
                  size="small"
                  fullWidth
                  value={confirmedFico}
                  onChange={(e) => setConfirmedFico(e.target.value)}
                  inputProps={{ min: 300, max: 850 }}
                  helperText="300–850"
                  sx={{ mb: 1.5 }}
                />
              </>
            )}
            <TextField
              label="Target price (points)"
              type="number"
              size="small"
              fullWidth
              value={targetPrice}
              onChange={(e) => setTargetPrice(e.target.value)}
              placeholder="100.000"
              helperText="Optional — picks the rate closest to this price (100 = par)"
              sx={{ mb: 1 }}
            />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="contained"
                disabled={!ficoValid}
                onClick={runPreview}
              >
                {hasRecommendation ? 'Update recommendation' : 'Analyze loan'}
              </Button>
              {(pricingError || !hasRecommendation) && (
                <Button variant="outlined" onClick={() => setManualMode(true)}>
                  Choose product manually
                </Button>
              )}
            </Stack>
          </Box>
        )}

        {loading && (
          <Stack alignItems="center" py={4}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Analyzing your MISMO file…
            </Typography>
          </Stack>
        )}

        {analysis && !loading && (
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <AutoAwesomeIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1" fontWeight={700}>
                  We analyzed the data in your loan file
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {analysis.borrowerName} · {analysis.loanPurpose} · {analysis.propertySummary}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">Loan amount</Typography>
                  <Typography variant="body2" fontWeight={600}>{fmtDollar(analysis.loanAmount)}</Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">LTV</Typography>
                  <Typography variant="body2" fontWeight={600}>{analysis.ltvPercent?.toFixed(2)}%</Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">Income / debts</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {fmtDollar(analysis.monthlyIncome)} / {fmtDollar(analysis.monthlyDebts)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">Est. back-end DTI</Typography>
                  <Typography variant="body2" fontWeight={600}>{analysis.previewBackEndDtiPercent?.toFixed(2)}%</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {canUseRecommendation && !loading && (
          <Card sx={{ mb: 2, borderColor: 'primary.main', borderWidth: 1, borderStyle: 'solid' }}>
            <CardContent>
              <Typography variant="overline" color="primary">Recommended program</Typography>
              <Typography variant="h6" fontWeight={700}>{recommendation.productName}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {recommendation.family} · {recommendation.term}-year
              </Typography>
              <Typography variant="h4" fontWeight={800} color="primary.main" sx={{ mb: 0.5 }}>
                {fmtPricingPct(recommendation.rate)}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Price {recommendation.adjustedPrice?.toFixed(3)} pts
                {recommendation.monthlyPayment > 0 && (
                  <> · Est. payment {fmtDollar(recommendation.monthlyPayment)}/mo</>
                )}
              </Typography>
              {pricingQuotedAt && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Pricing last updated {fmtDateTime(pricingQuotedAt)}
                </Typography>
              )}
              <Stack component="ul" sx={{ pl: 2, m: 0 }} spacing={0.5}>
                {(recommendation.rationale || []).map((line, i) => (
                  <Typography key={i} component="li" variant="body2" color="text.secondary">{line}</Typography>
                ))}
              </Stack>
            </CardContent>
          </Card>
        )}

        {!hasRecommendation && preview && !needsFico && !loading && !manualMode && !pricingError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No eligible product matched this scenario.
            {preview.coachingTips?.length > 0 && (
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                {preview.coachingTips.map((t, i) => (
                  <li key={i}><Typography variant="body2">{t}</Typography></li>
                ))}
              </Box>
            )}
          </Alert>
        )}

        {disagreeMode && recommendedProduct && !manualMode && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Choose a different rate for {recommendedProduct.productName}
            </Typography>
            <RateGrid
              product={recommendedProduct}
              loanAmount={analysis?.loanAmount || 0}
              selectedRate={overrideRate}
              onSelectRate={setOverrideRate}
              showAllRates={showAllRates}
              onToggleShowAll={() => setShowAllRates((v) => !v)}
            />
          </Box>
        )}

        {manualMode && !loading && (
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                Choose product manually
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Pre-import Optimal Blue analysis needs a loan. Import this file, load products from the LOS,
                then pick a product, rate, price, and lock term.
              </Typography>

              {needsFico && (
                <TextField
                  label="Representative FICO"
                  type="number"
                  size="small"
                  fullWidth
                  value={confirmedFico}
                  onChange={(e) => setConfirmedFico(e.target.value)}
                  inputProps={{ min: 300, max: 850 }}
                  helperText="Required before import pricing"
                  sx={{ mb: 1.5 }}
                />
              )}

              {!importedLoanId && (
                <Button
                  variant="contained"
                  disabled={busy || !ficoValid}
                  onClick={handleLoadProducts}
                  sx={{ mb: 2 }}
                >
                  {busy ? 'Importing…' : 'Import loan & load products'}
                </Button>
              )}

              {importedLoanId && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Loan imported{importedLoanNumber ? ` (#${importedLoanNumber})` : ''}. Select a product below.
                </Alert>
              )}

              {importedLoanId && manualProducts.length === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No Optimal Blue products were returned. Adjust filters and try Import loan & load products again.
                </Alert>
              )}

              {manualProducts.length > 0 && (
                <>
                  <TextField
                    select
                    label="Product"
                    size="small"
                    fullWidth
                    value={selectedProductId}
                    onChange={(e) => {
                      const product = manualProducts.find((p) => p.productId === e.target.value);
                      applyProductDefaults(product, manualLockTerm, targetPrice);
                    }}
                    sx={{ mb: 1.5 }}
                  >
                    {manualProducts.map((p) => (
                      <MenuItem key={p.productId} value={p.productId}>
                        {p.productName || p.productShortName}
                        {p.isEligible === false ? ' (ineligible)' : ''}
                      </MenuItem>
                    ))}
                  </TextField>

                  {selectedManualProduct && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        Select an Optimal Blue rate
                      </Typography>
                      <RateGrid
                        product={selectedManualProduct}
                        loanAmount={analysis?.loanAmount || 0}
                        selectedRate={selectedManualRate}
                        onSelectRate={(row) => {
                          setSelectedManualRate(row);
                          setManualRate(String(row.rate ?? ''));
                          setManualPrice(String(row.adjustedPrice ?? ''));
                          if (row.lockTerm) setManualLockTerm(row.lockTerm);
                        }}
                        showAllRates={showAllManualRates}
                        onToggleShowAll={() => setShowAllManualRates((v) => !v)}
                      />
                    </Box>
                  )}
                </>
              )}

              {hasRecommendation && (
                <Button size="small" sx={{ mt: 2 }} onClick={() => setManualMode(false)}>
                  Back to recommendation
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button color="inherit" onClick={onBack} disabled={busy}>Back</Button>
        <Box sx={{ flex: 1 }} />
        {!manualMode && (
          <>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<ThumbDownIcon />}
              onClick={handleDisagree}
              disabled={busy || (!hasRecommendation && !disagreeMode)}
            >
              {disagreeMode ? 'Continue with my rate' : 'I disagree'}
            </Button>
            <Button
              variant="contained"
              startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <ThumbUpIcon />}
              onClick={handleAgree}
              disabled={busy || !hasRecommendation || (disagreeMode && !overrideRate)}
            >
              {busy ? 'Working…' : 'I agree — use this product & rate'}
            </Button>
          </>
        )}
        {manualMode && (
          <Button
            variant="contained"
            startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <ThumbUpIcon />}
            onClick={handleManualConfirm}
            disabled={
              busy
              || !importedLoanId
              || !selectedManualProduct
              || !selectedManualRate?.quoteId
            }
          >
            {busy ? 'Working…' : 'Assign product & continue'}
          </Button>
        )}
      </DialogActions>
    </>
  );
};

export default MismoAdvisorStep;
export { ADVISOR_DISCLAIMER };
